var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spread = (this && this.__spread) || function () {
    for (var ar = [], i = 0; i < arguments.length; i++) ar = ar.concat(__read(arguments[i]));
    return ar;
};
import isPlainObject from 'lodash/isPlainObject';
import { isValidElement } from 'react';
/**
 * Combine props object together
 *
 * that will group className, classes together
 * function will auto combine together,
 * trigger order is `intoProps.someFn(e) => defaultProps.someFn(e)`
 *
 * @param baseProps base props you want to combine together
 * @param newProps want to combine in to props
 * @param coverPropKeys some prop key you want to cover, not combine when type is function, ex. `ComponentType`
 *
 *
 * @example
 * ```tsx
 * combineProps(
    {
      children: <A />,
      value: '1',
      className: 'a1',
      classes: {
        root: 'root1'
      },
      count: 1,
      size: 'large',
      onClick: fn1
    },
    {
      children: <B />,
      value: '2',
      className: 'a2',
      classes: {
        root: 'root2'
      },
      count: 2,
      size: undefined,
      onClick: fn2
    },
  )
  // output result:
  {
    children: <B />,
    value: '2',
    className: 'a1 a2',
    classes: {
      root: 'root1 root2'
    },
    count: 2,
    size: 'large',
    onClick: (e) => {
      fn2(e);
      fn1(e);
    }
  }
 * ```
 */
export var combineProps = function (baseProps, newProps, coverPropKeys) {
    return innerCombineProps({
        baseProps: baseProps,
        newProps: newProps,
        coverPropKeys: coverPropKeys,
    });
};
/**
 * Combine two classes
 *
 * @param baseClasses base classes you want to combine together
 * @param newClasses new classes want to combine in to props
 *
 * @example
 * ```ts
 * combineClasses(
    {
      root: 'a1',
      input: 'a1',
      un1: undefined,
      un2: 'a4',
    },
    {
      root: 'a1',
      input: 'a2',
      un1: 'a3',
      un2: undefined,
      un3: 'a5',
    },
  )
  // output result:
  {
    root: 'a1 a1',
    input: 'a1 a2',
    un1: 'a3',
    un2: 'a4',
    un3: 'a5',
  }
 * ```
 */
export var combineClasses = function (baseClasses, newClasses) {
    return innerCombineProps({
        baseProps: baseClasses,
        newProps: newClasses,
        combineAllString: true,
    });
};
var innerCombineProps = function (params) {
    var baseProps = params.baseProps, newProps = params.newProps;
    if (!newProps || Object.keys(newProps).length === 0) {
        return baseProps;
    }
    var outputProps = mergeChildProps(params);
    return __assign(__assign({}, newProps), outputProps);
};
function mergeChildProps(_a) {
    var baseProps = _a.baseProps, newProps = _a.newProps, combineAllString = _a.combineAllString, coverPropKeys = _a.coverPropKeys;
    var result = Object.keys(baseProps || {}).reduce(function (output, curr) {
        var prop = baseProps[curr];
        var propTo = newProps[curr];
        if (propTo !== undefined) {
            // * all typeof type below
            // "string" | "number" | "bigint" | "boolean" | "symbol" | "undefined" | "object" | "function"
            switch (typeof prop) {
                case 'undefined':
                    output[curr] = propTo;
                    break;
                case 'object': {
                    if (!isValidElement(propTo) &&
                        !isValidElement(prop) &&
                        isPlainObject(propTo) &&
                        isPlainObject(prop)) {
                        output[curr] = mergeChildProps({
                            baseProps: prop,
                            newProps: propTo || {},
                            combineAllString: combineAllString || curr === 'classes',
                        });
                    }
                    else {
                        output[curr] = propTo;
                    }
                    break;
                }
                case 'string':
                    if (combineAllString || curr === 'className') {
                        output[curr] = [prop, propTo]
                            .filter(function (x) { return typeof x === 'string' && x; })
                            .join(' ');
                    }
                    else {
                        output[curr] = propTo;
                    }
                    break;
                case 'boolean':
                case 'symbol':
                case 'bigint':
                case 'number':
                    output[curr] = propTo;
                    break;
                case 'function':
                    if (coverPropKeys && coverPropKeys.includes(curr) && propTo) {
                        output[curr] = propTo;
                    }
                    else {
                        output[curr] = function () {
                            var args = [];
                            for (var _i = 0; _i < arguments.length; _i++) {
                                args[_i] = arguments[_i];
                            }
                            // * exec outside first
                            propTo === null || propTo === void 0 ? void 0 : propTo.apply(void 0, __spread(args));
                            prop === null || prop === void 0 ? void 0 : prop.apply(void 0, __spread(args));
                        };
                    }
                    break;
                default:
            }
        }
        else {
            output[curr] = prop;
        }
        return output;
    }, {});
    // if result is an empty {} reset that to undefined
    if (Object.keys(result).length === 0) {
        return undefined;
    }
    return __assign(__assign({}, newProps), result);
}
